import React from 'react';
import {
  Form,
  Space,
  Button,
  Drawer,
  Spin,
  Modal,
  Typography,
  message,
  Row,
  Col,
  Grid,
  Popconfirm,
  Tabs,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useDelete } from '@refinedev/core';
import { FormField, FormDisplayMode } from '../../../SmartDataTable/types';
import { FormItemRenderer } from './FormItemRenderer';
import { formatNumber } from '@src/utils/numberUtils';
import { cleanPhoneNumber, formatPhoneNumberWithPrefix } from '@src/utils/phoneUtils';
import { ChatComponent } from '../../../Chat/ChatComponent';
import './styles.css';

const { useBreakpoint } = Grid;

interface CommonFormProps {
  // Form configuration
  fields: FormField[];
  title?: string;
  submitText?: string;
  initialValues?: Record<string, any>;

  // Display configuration
  formDisplayMode: FormDisplayMode;
  open: boolean;
  drawerWidth?: number | string;
  modalWidth?: number | string;

  // Form state
  form: any;
  formProps: any;
  saveButtonProps: any;
  formLoading?: boolean;

  // Callbacks
  onClose: () => void;
  onFinish?: (values: any) => void;
  onFinishFailed?: (errorInfo: any) => void;

  // Additional props
  resourceName?: string;
  loadingText?: string;

  // Chat configuration
  enableChat?: boolean;
  chatType?: string;
  chatRelativeId?: number;
  chatPatientId?: number; // Optional patient ID for chat context
  chatTitle?: string;
  chatPlaceholder?: string;

  // Custom title component
  customTitle?: React.ReactNode;

  /** Optional second tab wrapping the main form (e.g. Patient next to appointment fields). */
  formSecondaryTab?: {
    mainLabel?: string;
    label: string;
    children: React.ReactNode;
  };

  // Auto-scroll configuration
  isintialScroll?: boolean;

  // Delete configuration
  showDelete?: boolean;
  deleteResource?: string;
  deleteId?: string | number;
  onDeleteSuccess?: () => void;
  rowKey?: string;
}

export const CommonForm: React.FC<CommonFormProps> = ({
  fields,
  title,
  submitText,
  initialValues,
  formDisplayMode,
  open,
  drawerWidth = 720,
  modalWidth = 720,
  form,
  formProps,
  saveButtonProps,
  formLoading = false,
  onClose,
  onFinish,
  onFinishFailed,
  resourceName,
  loadingText = 'Loading record data...',
  enableChat = false,
  chatType,
  chatRelativeId,
  chatPatientId,
  chatTitle = 'Recent Messages',
  chatPlaceholder = 'Type your message...',
  customTitle,
  formSecondaryTab,
  isintialScroll,
  showDelete = false,
  deleteResource,
  deleteId,
  onDeleteSuccess,
  rowKey = 'id',
}) => {
  const screens = useBreakpoint();
  const smallScreen = !screens.md; // md breakpoint is 768px

  // Delete functionality
  const { mutate: deleteRecord } = useDelete();

  const handleDelete = () => {
    if (!deleteResource || !deleteId) {
      message.error('Cannot delete: Missing resource or ID');
      return;
    }

    deleteRecord(
      {
        resource: deleteResource,
        id: deleteId,
      },
      {
        onSuccess: () => {
          message.success('Record deleted successfully');
          onClose();
          onDeleteSuccess?.();
        },
        onError: (error: any) => {
          message.error(
            error?.response?.data?.message || 'Failed to delete record'
          );
        },
      }
    );
  };

  // Transform SearchSelect values and mobile numbers to the expected format and ensure all fields are included
  const transformFormValues = (values: any) => {
    const transformedValues: any = { ...values };

    // Helper function to check if a field is a mobile number field
    const isMobileNumberField = (fieldName: string): boolean => {
      const mobileFieldPatterns = [
        'mobile_no',
        'mobile',
        'phone',
        'patient_mobile_no',
        'customer_phone',
      ];
      return mobileFieldPatterns.some(pattern =>
        fieldName.toLowerCase().includes(pattern.toLowerCase())
      );
    };

    // Transform mobile numbers: clean first, then add '91' prefix for submission
    // Note: Skip fields that have their own transform (they will handle it themselves)
    Object.keys(transformedValues).forEach(key => {
      const value = transformedValues[key];
      if (isMobileNumberField(key) && value !== null && value !== undefined && value !== '') {
        // Check if this field has its own transform - if so, skip CommonForm transformation
        const field = fields.find(f => f.name === key);
        if (!field || !field.transform) {
          // Only transform if field doesn't have its own transform
          // First clean the number, then format with '91' prefix for backend
          const formatted = formatPhoneNumberWithPrefix(value);
          transformedValues[key] = formatted;
        }
      }
    });

    // Helper function to get default value based on field type
    const getDefaultValueByType = (field: FormField): any => {
      // If field has a specific defaultValue, use it
      if (field.defaultValue !== undefined) {
        return field.defaultValue;
      }

      // Otherwise, use type-specific defaults
      switch (field.type) {
        case 'text':
        case 'textarea':
        case 'email':
        case 'password':
        case 'telephone':
        case 'markdown':
        case 'richtext':
          return '';
        case 'number':
          // Handle decimal formatting for number fields
          if (field.format === 'decimal' && field.decimalPlaces !== undefined) {
            return formatNumber(0, field.decimalPlaces);
          }
          return 0;
        case 'select':
        case 'resourceSelect':
        case 'multiselect':
          return null;
        case 'date':
        case 'datetime':
          return null;
        case 'checkbox':
        case 'switch':
          return false;
        case 'radio':
          return null;
        case 'file':
          return null;
        case 'table':
          return [];
        default:
          return '';
      }
    };

    // Helper function to get nested value safely
    const getNestedValue = (obj: any, path: string): any => {
      const keys = path.split('.');
      let current = obj;
      for (const key of keys) {
        if (current && typeof current === 'object' && key in current) {
          current = current[key];
        } else {
          return undefined;
        }
      }
      return current;
    };

    // Helper function to set nested value safely
    const setNestedValue = (obj: any, path: string, value: any): void => {
      const keys = path.split('.');
      let current = obj;
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!(key in current) || typeof current[key] !== 'object') {
          current[key] = {};
        }
        current = current[key];
      }
      current[keys[keys.length - 1]] = value;
    };

    fields.forEach(field => {
      const fieldName = field.name;
      const isNested = fieldName.includes('.');

      // Skip customTitle fields as they are display-only
      if (field.type === 'customTitle') {
        return;
      }

      // Get current value (either from transformed values or undefined)
      let fieldValue = isNested
        ? getNestedValue(transformedValues, fieldName)
        : transformedValues[fieldName];

      // If field value is undefined (not touched by user), set appropriate default
      if (fieldValue === undefined) {
        const defaultValue = getDefaultValueByType(field);
        if (isNested) {
          setNestedValue(transformedValues, fieldName, defaultValue);
        } else {
          transformedValues[fieldName] = defaultValue;
        }
        fieldValue = defaultValue;
      }

      // Handle SearchSelect transformation
      if (field.type === 'resourceSelect' && fieldValue) {
        if (typeof fieldValue === 'object' && fieldValue.value !== undefined) {
          const transformedValue = fieldValue.value;

          if (isNested) {
            setNestedValue(transformedValues, fieldName, transformedValue);
          } else {
            transformedValues[fieldName] = transformedValue;
          }
        }
      }

      // Handle specific type transformations
      if (
        field.type === 'number' &&
        fieldValue !== null &&
        fieldValue !== undefined
      ) {
        const numValue = Number(fieldValue);
        if (!isNaN(numValue)) {
          // Handle decimal formatting
          let finalValue = numValue;
          if (field.format === 'decimal' && field.decimalPlaces !== undefined) {
            finalValue = formatNumber(numValue, field.decimalPlaces);
          }

          if (isNested) {
            setNestedValue(transformedValues, fieldName, finalValue);
          } else {
            transformedValues[fieldName] = finalValue;
          }
        }
      }

      // Handle boolean types
      if (
        (field.type === 'checkbox' || field.type === 'switch') &&
        fieldValue !== null &&
        fieldValue !== undefined
      ) {
        const boolValue = Boolean(fieldValue);
        if (isNested) {
          setNestedValue(transformedValues, fieldName, boolValue);
        } else {
          transformedValues[fieldName] = boolValue;
        }
      }

      // Handle array types
      if (field.type === 'multiselect' && !Array.isArray(fieldValue)) {
        const arrayValue = fieldValue ? [fieldValue] : [];
        if (isNested) {
          setNestedValue(transformedValues, fieldName, arrayValue);
        } else {
          transformedValues[fieldName] = arrayValue;
        }
      }

      // Handle custom field transformations
      if (field.transform) {
        let transformedValue = fieldValue;
        
        // Handle transform as object with input/output
        if (typeof field.transform === 'object' && 'output' in field.transform) {
          // Apply output transform for form submission (always apply, handles null/empty cases)
          transformedValue = field.transform.output(fieldValue);
          if (isNested) {
            setNestedValue(transformedValues, fieldName, transformedValue);
          } else {
            transformedValues[fieldName] = transformedValue;
          }
        } 
        // Handle transform as function (backward compatibility)
        else if (typeof field.transform === 'function' && fieldValue !== null && fieldValue !== undefined) {
          transformedValue = field.transform(fieldValue);
          if (transformedValue !== fieldValue) {
            if (isNested) {
              setNestedValue(transformedValues, fieldName, transformedValue);
            } else {
              transformedValues[fieldName] = transformedValue;
            }
          }
        }
      }
    });

    // Final cleanup: Convert empty strings to null for optional fields that should be null
    // This handles fields like secondary_mobile_number, address, notes, etc.
    fields.forEach(field => {
      const fieldName = field.name;
      const fieldValue = transformedValues[fieldName];
      
      // Convert empty strings to null for optional fields (fields that can be null)
      if (
        !field.required &&
        typeof fieldValue === 'string' &&
        fieldValue.trim() === ''
      ) {
        // For certain fields like mobile numbers, addresses, notes - convert empty to null
        const shouldBeNull = 
          fieldName.includes('secondary') ||
          (field.defaultValue === null);
        
        if (shouldBeNull) {
          transformedValues[fieldName] = null;
        }
      }
      
      // Convert 0 to null for optional select fields (handles edge cases where 0 might be set)
      if (
        !field.required &&
        (field.type === 'select' || field.type === 'resourceSelect') &&
        fieldValue === 0
      ) {
        transformedValues[fieldName] = null;
      }
    });

    return transformedValues;
  };

  const handleFinish = (values: any) => {
    const transformedValues = transformFormValues(values);
    return onFinish?.(transformedValues);
  };

  const handleFinishFailed = (errorInfo: any) => {
    message.error('Please check the form for errors.');
    onFinishFailed?.(errorInfo);
  };

  const renderForm = () => (
    <Form
      form={form}
      layout="vertical"
      initialValues={initialValues}
      validateTrigger={['onBlur', 'onSubmit']}
      onFinish={handleFinish}
      onFinishFailed={handleFinishFailed}
      onValuesChange={(changedValues, allValues) => {
        // Form values changed
      }}
      style={
        {
          // Global form styling
          '--form-border-radius': '8px',
          '--form-border-color': '#d9d9d9',
          '--form-focus-color': '#1890ff',
          '--form-hover-color': '#40a9ff',
        } as React.CSSProperties
      }
      className="smart-data-table-form"
    >
      <Row gutter={[16, 0]}>
      {fields.map(field => {
        // Check if this field needs conditional rendering based on outcome or source
        if (field.name === 'new_follow_up_date') {
          return (
            <Form.Item
              key={field.name}
              shouldUpdate={(prevValues, currentValues) => {
                return prevValues.outcome !== currentValues.outcome || prevValues.type !== currentValues.type;
              }}
              noStyle
            >
              {({ getFieldValue }) => {
                const outcome = getFieldValue('outcome');
                const type = getFieldValue('type');
                const outcomesRequiringFollowUp = [
                  'NEED_TIME',
                  'INTERESTED',
                  'NO_RESPONSE',
                  'NOT_REACHABLE',
                  'SWITCHED_OFF',
                  'DO_NOT_DISTURB',
                  'CALL_BACK_LATER',
                  'FOLLOW_UP'
                ];
                
                // Don't show if type is APPOINTMENT and outcome is NEED_TIME
                const isAppointmentNeedTime = type === 'APPOINTMENT' && outcome === 'NEED_TIME';
                const shouldShow = outcome && outcomesRequiringFollowUp.includes(outcome) && !isAppointmentNeedTime;
                
                if (!shouldShow) {
                  return null;
                }
                
                return (
                  <Col
                    span={field.layout?.span || 24}
                    offset={field.layout?.offset}
                    pull={field.layout?.pull}
                    push={field.layout?.push}
                  >
                    <FormItemRenderer field={{...field, hidden: false}} form={form} />
                  </Col>
                );
              }}
            </Form.Item>
          );
        }
        
         // Check if this field needs conditional rendering based on source (for leads only)
         if (field.name === 'referred_by_name' && title?.includes('Lead')) {
           return (
             <Form.Item
               key={field.name}
               shouldUpdate={(prevValues, currentValues) => {
                 return prevValues.source !== currentValues.source;
               }}
               noStyle
             >
               {({ getFieldValue }) => {
                 const source = getFieldValue('source');
                 const shouldShow = source === 'REFERRAL';
                 
                 if (!shouldShow) {
                   return null;
                 }
                 
                 return (
                   <Col
                     span={field.layout?.span || 24}
                     offset={field.layout?.offset}
                     pull={field.layout?.pull}
                     push={field.layout?.push}
                   >
                     <FormItemRenderer field={{...field, hidden: false}} form={form} />
                   </Col>
                 );
               }}
             </Form.Item>
           );
         }

         // Check if this field needs conditional rendering for new appointment schedule date
         if (field.name === 'new_appointment_schedule_date') {
           return (
             <Form.Item
               key={field.name}
               shouldUpdate={(prevValues, currentValues) => {
                 return prevValues.type !== currentValues.type || 
                        prevValues.outcome !== currentValues.outcome;
               }}
               noStyle
             >
               {({ getFieldValue }) => {
                 const type = getFieldValue('type');
                 const outcome = getFieldValue('outcome');
                 const shouldShow = type === 'APPOINTMENT' && outcome === 'NEED_TIME';
                 
                 if (!shouldShow) {
                   return null;
                 }
                 
                 return (
                   <Col
                     span={field.layout?.span || 24}
                     offset={field.layout?.offset}
                     pull={field.layout?.pull}
                     push={field.layout?.push}
                   >
                     <FormItemRenderer field={{...field, hidden: false}} form={form} />
                   </Col>
                 );
               }}
             </Form.Item>
           );
         }

         // Check if this field needs conditional rendering for next appointment date
         if (field.name === 'next_appointment_date') {
           return (
             <Form.Item
               key={field.name}
               shouldUpdate={(prevValues, currentValues) => {
                 return prevValues.status !== currentValues.status;
               }}
               noStyle
             >
               {({ getFieldValue }) => {
                 const status = getFieldValue('status');
                 // Show next appointment date when status is COMPLETED or CANCELLED
                 const shouldShow = status === 'COMPLETED' || status === 'CANCELLED';
                 
                 if (!shouldShow) {
                   return null;
                 }
                 
                 return (
                   <Col
                     span={field.layout?.span || 24}
                     offset={field.layout?.offset}
                     pull={field.layout?.pull}
                     push={field.layout?.push}
                   >
                     <FormItemRenderer field={{...field, hidden: false}} form={form} />
                   </Col>
                 );
               }}
             </Form.Item>
           );
         }

         // Check if this field needs conditional rendering for is_create_patient or is_create_appointment (for leads only)
         if ((field.name === 'is_create_patient' || field.name === 'is_create_appointment') && title?.includes('Lead')) {
           return (
             <Form.Item
               key={field.name}
               shouldUpdate={(prevValues, currentValues) => {
                 return prevValues.status !== currentValues.status;
               }}
               noStyle
             >
               {({ getFieldValue }) => {
                 const status = getFieldValue('status');
                 const shouldShow = ['CONVERTED', 'CLINIC_VISITED','NOT_CONVERTED'].includes(status);
                 
                 if (!shouldShow) {
                   return null;
                 }
                 
                 return (
                   <Col
                     span={field.layout?.span || 24}
                     offset={field.layout?.offset}
                     pull={field.layout?.pull}
                     push={field.layout?.push}
                   >
                     <FormItemRenderer field={{...field, hidden: false}} form={form} />
                   </Col>
                 );
               }}
             </Form.Item>
           );
         }
        
        return (
          <Col
            key={field.name}
            span={field.layout?.span || 24}
            offset={field.layout?.offset}
            pull={field.layout?.pull}
            push={field.layout?.push}
          >
            <FormItemRenderer field={field} form={form} />
          </Col>
        );
      })}
      </Row>
    </Form>
  );

  const renderFormTabs = () => {
    if (!formSecondaryTab) {
      return renderForm();
    }
    const mainLabel = formSecondaryTab.mainLabel ?? 'Details';
    return (
      <Tabs
        defaultActiveKey="main"
        items={[
          {
            key: 'main',
            label: mainLabel,
            children: (
              <div style={{ paddingTop: 8 }}>{renderForm()}</div>
            ),
          },
          {
            key: 'secondary',
            label: formSecondaryTab.label,
            children: (
              <div style={{ paddingTop: 8 }}>{formSecondaryTab.children}</div>
            ),
          },
        ]}
      />
    );
  };

  const renderFooter = () => (
    <Space style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
      <div style={{ display: 'flex', gap: '10px' }}>
      <Button onClick={onClose}>Cancel</Button>
      
      <Button type="primary" {...saveButtonProps} loading={formLoading}>
        {submitText || 'Save'}
      </Button>

      </div>

      {showDelete && deleteResource && deleteId && (
        <Popconfirm
          title="Delete Record"
          description="Are you sure you want to delete this record? This action cannot be undone."
          onConfirm={handleDelete}
          okText="Delete"
          cancelText="Cancel"
          okType="danger"
        >
          <Button danger icon={<DeleteOutlined />}>
            Delete
          </Button>
        </Popconfirm>
      )}
    </Space>
  );

  const hasChatLayout = enableChat && !!chatType && !!chatRelativeId;

  const renderContent = () => {
    if (formLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <Typography.Text style={{ marginTop: '16px', display: 'block' }}>
            {loadingText}
          </Typography.Text>
        </div>
      );
    }

    if (hasChatLayout) {
      const chatSplitMaxHeight = 'calc(100vh - 188px)';
      return (
        <div
          className="common-form-chat-split"
          style={{
            height: chatSplitMaxHeight,
            maxHeight: chatSplitMaxHeight,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Row
            gutter={[16, 16]}
            style={{
              flex: 1,
              minHeight: 0,
              height: '100%',
              margin: 0,
              overflow: 'hidden',
            }}
          >
            <Col
              xs={24}
              sm={24}
              md={16}
              style={{
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <div className="common-form-chat-form-column">
                {renderFormTabs()}
              </div>
            </Col>
            <Col
              xs={24}
              sm={12}
              md={8}
              style={{
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <div className="chat-column-wrapper">
                <ChatComponent
                  type={chatType}
                  relativeId={chatRelativeId}
                  patientId={chatPatientId}
                  title={chatTitle}
                  placeholder={chatPlaceholder}
                  height="100%"
                  isintialScroll={smallScreen ? false : isintialScroll}
                />
              </div>
            </Col>
          </Row>
        </div>
      );
    }

    return renderFormTabs();
  };

  if (formDisplayMode === 'modal') {
    return (
      <Modal
        title={
          customTitle ||
          title ||
          `${submitText || 'Save'} ${resourceName || 'Record'}`
        }
        open={open}
        onCancel={onClose}
        footer={renderFooter()}
        width={modalWidth}
      >
        {renderContent()}
      </Modal>
    );
  }

  return (
    <Drawer
      title={
        customTitle ||
        title ||
        `${submitText || 'Save'} ${resourceName || 'Record'}`
      }
      open={open}
      onClose={onClose}
      width={drawerWidth}
      footer={renderFooter()}
      className={hasChatLayout ? 'common-form-drawer-with-chat' : undefined}
      styles={
        hasChatLayout
          ? {
              body: {
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                paddingBottom: 16,
              },
            }
          : undefined
      }
    >
      {renderContent()}
    </Drawer>
  );
};
